/**
 * <color-finder>
 *
 * Shopper picks a fabric color; the pre-rendered grid narrows to the beds sold in it, and each card's
 * image and links switch to that color (the image tagged DM-COLOR-<COLOR>, linking to that color's
 * variant). Each item carries its colors as JSON: { key: { variant, src, srcset, alt } }.
 * The cap (data-max-results, or data-max-results-mobile below 750px) works like the softness finder's.
 */
class ColorFinder extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-cf-grid]');
    this.swatches = Array.from(this.querySelectorAll('[data-cf-swatch]'));
    if (!this.grid || !this.swatches.length) return;

    this.countOutput = this.querySelector('[data-cf-count]');
    this.emptyMessage = this.querySelector('[data-cf-empty]');
    this.toggleButton = this.querySelector('[data-cf-toggle]');
    this.toggleText = this.querySelector('[data-cf-toggle-text]');
    this.strings = this.parseJSON(this.querySelector('[data-cf-strings]'), {});
    this.maxResults = parseInt(this.dataset.maxResults, 10) || 0;
    this.maxResultsMobile = parseInt(this.dataset.maxResultsMobile, 10) || this.maxResults;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.expanded = false;

    this.items = Array.from(this.querySelectorAll('[data-cf-item]')).map((element) => ({
      element,
      url: element.dataset.cfUrl,
      colors: this.parseJSON(element.querySelector('[data-cf-colors]'), {}),
    }));

    this.swatches.forEach((swatch) => swatch.addEventListener('click', () => this.select(swatch)));
    this.querySelector('[role="radiogroup"]').addEventListener('keydown', this.onKeydown.bind(this));
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.render();
      });
    }
    this.mobileQuery.addEventListener?.('change', () => this.render());

    const initial = this.swatches.find((swatch) => swatch.getAttribute('aria-checked') === 'true') || this.swatches[0];
    this.select(initial, false);
  }

  select(swatch, focus = false) {
    this.swatches.forEach((other) => {
      const active = other === swatch;
      other.setAttribute('aria-checked', active);
      other.tabIndex = active ? 0 : -1;
    });
    if (focus) swatch.focus();
    this.color = swatch.dataset.cfSwatch;
    this.colorLabel = swatch.dataset.cfLabel;
    this.expanded = false;
    this.render();
  }

  onKeydown(event) {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const rtl = getComputedStyle(this).direction === 'rtl' && (event.key === 'ArrowRight' || event.key === 'ArrowLeft');
    const index = this.swatches.findIndex((swatch) => swatch.getAttribute('aria-checked') === 'true');
    const next = (index + (rtl ? -step : step) + this.swatches.length) % this.swatches.length;
    this.select(this.swatches[next], true);
  }

  render() {
    const matches = this.items.filter((item) => item.colors[this.color]);
    const cap = this.mobileQuery.matches ? this.maxResultsMobile : this.maxResults;
    const limit = this.expanded || !cap ? matches.length : cap;

    this.items.forEach((item) => {
      const data = item.colors[this.color];
      const visible = Boolean(data) && matches.indexOf(item) < limit;
      item.element.hidden = !visible;
      if (data) this.applyColor(item, data);
    });

    if (this.emptyMessage) this.emptyMessage.hidden = matches.length > 0;
    if (this.countOutput) {
      const template = matches.length === 1 ? this.strings.count_one : this.strings.count_other;
      this.countOutput.textContent = matches.length
        ? (template || '').replace('{{ count }}', matches.length).replace('{{ color }}', this.colorLabel)
        : '';
    }
    if (this.toggleButton) {
      const capped = cap && matches.length > cap;
      this.toggleButton.hidden = !capped;
      this.toggleButton.setAttribute('aria-expanded', this.expanded);
      if (this.toggleText) this.toggleText.textContent = this.expanded ? this.strings.collapse : this.strings.expand;
    }
  }

  applyColor(item, data) {
    if (item.applied === this.color) return;
    item.applied = this.color;

    item.element.querySelectorAll('.card__media img').forEach((image, index) => {
      if (index > 0) return; // only the main image; secondary hover images are off in this section
      image.src = data.src;
      image.srcset = data.srcset;
      image.alt = data.alt;
    });

    const href = `${item.url}?variant=${data.variant}`;
    item.element.querySelectorAll(`a[href^="${item.url}"]`).forEach((link) => link.setAttribute('href', href));
    item.element.querySelectorAll('input[name="id"]').forEach((input) => (input.value = data.variant));
  }

  parseJSON(element, fallback) {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return fallback;
    }
  }
}

if (!customElements.get('color-finder')) customElements.define('color-finder', ColorFinder);
